
import { mapState, mapGetters } from 'vuex'
import { toHead } from 'vue-datocms'
import { homeQuery } from '~/gql/homepage.graphql'
import HomeHero from '~/components/page-home/home-hero.vue'
import HomeCarousel from '~/components/page-home/home-carousel.vue'
import CompanyUsps from '~/components/page-home/company-usps.vue'
import CompanyFeatures from '~/components/page-home/company-features.vue'
import FeaturedGroup from '~/components/public/featured/featured-group.vue'
import CompanyStats from '~/components/page-home/company-stats.vue'
import { getProductName, getProductImageUrl } from '~/utils/product-info'
import { createChartSeries } from '@/utils/chart-series'

export default {
  components: {
    HomeHero,
    HomeCarousel,
    CompanyStats,
    CompanyUsps,
    CompanyFeatures,
    FeaturedGroup,
  },
  layout: 'default',
  meta: {
    isPublic: true,
  },
  async asyncData({ $graphql, error, i18n, store }) {
    const variables = {
      locale: i18n.locale,
    }

    try {
      const pageResponse = await $graphql.default.request(homeQuery, variables)

      await Promise.all([
        store.dispatch('homepage/getStats'),
        store.dispatch('homepage/getProducts'),
      ])

      const {
        homepageRedesign: {
          hero,
          carousel,
          about,
          usp,
          features,
          readMore,
          beforeYouStart,
          seo,
        },
      } = pageResponse

      return {
        hero,
        carousel,
        companyAbout: about,
        companyUsps: usp,
        companyFeatures: features,
        featuredItems: readMore.links,
        beforeYouStart,
        seo,
      }
    } catch (e) {
      console.error(e)

      return error({
        statusCode: 500,
        message: 'There was an error loading the page',
      })
    }
  },
  head() {
    if (!this.seo) {
      return
    }

    return toHead(this.seo)
  },
  computed: {
    ...mapState('homepage', ['products', 'stats']),
    ...mapGetters('homepage', ['hasStats', 'hasProducts']),

    statsFormatted() {
      const f = this.stats.map((stat) => {
        return {
          title: stat.title,
          previousMonth: Math.round(stat.previousMonth),
          performance: stat.performance,
          dateTwoMonthsAgo: stat.dateTwoMonthsAgo,
          twoMonthsAgo: Math.round(stat.twoMonthsAgo),
          charts: stat.charts
            .map((chart) => {
              return createChartSeries(chart.points, chart.title, {
                states: {
                  hover: {
                    enabled: false,
                  },
                },
              })
            })
            .reverse(),
        }
      })

      return f
    },

    productsFormatted() {
      return this.products.map((item) => ({
        title: getProductName(item.articleDetails, {
          includeBatchName: false,
        }),
        imageUrl: getProductImageUrl(item.articleDetails),
        id: item.articleDetails.itemId,
        marketPrice: item.marketPrice,
        minSellOrderPrice: item.minSellOrderPrice,
        slug: item.articleDetails.slug,
      }))
    },
  },

  methods: {
    isStatType(statType, unitOfMeasure) {
      return unitOfMeasure === statType
    },

    getFormattedValue(unitOfMeasure, value) {
      if (this.isStatType('Euro', unitOfMeasure)) {
        return this.$formatAsCurrency(value)
      }
      if (this.isStatType('Article', unitOfMeasure)) {
        return this.$formatAsNumber(value)
      }
      return value
    },
  },
}
